import { ModalContext } from '@/contexts/ModalProvider.jsx';
import React, { useEffect, useState, useContext, useMemo } from 'react';

import { SideCartEdit } from '@desquared/wave-components-library';
import Router, { useRouter } from 'next/router';
import { isUndefined } from 'lodash';
import { productsForOrder, fullCartStore } from '@/store/cart';
import { setCartProducts, checkOrderEdit, onAdd, onRemove, setDiscounts, onRemoveAll } from '@/utils/products/products';
import { openListSideSheet } from '@/utils/products';
import { ListAdditionSheetStore } from '@/store/sideSheet';
import { cartPrice } from '@/store/cart';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { useTranslations } from 'next-intl';
import EmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/Empty-Cart.png';
import getLayoutProps from '@/utils/layout/layout';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { useQueryClient } from '@tanstack/react-query';
import { getProductInfoV3, getProductDetailsV3 } from '@/api/Products';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage';
import AgeRestrictedProductsModal from '@/components/AgeRestrictedProductsModal/AgeRestrictedProductsModal.jsx';
import { useAgeVerificationModal } from '@/hooks/useAgeVerificationModal.js';
import { UserIsOfLegalAgeStore } from '@/store/userIsOfLegalAge.js';
import { useConfiguration } from '@/contexts/ConfigContext.jsx';

export default function SideCartEditComponent({ orderProducts, selectedOrder, onSelect, onOpenDetails }) {
  const router = useRouter();
  const { formatter } = useContext(CurrencyContext);
  const [tempProducts, setTempProducts] = useState([]);
  const [totalPrice, setTotalPrice] = useState('');
  const [image, setImage] = useState();
  const [minOrderImage, setMinOrderImage] = useState();
  const [remove, setRemove] = useState(true);
  const [formattedTitle, setFormattedTitle] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState();
  const [productsFiltered, setProductsFiltered] = useState([]);
  const { getCollectionTypes, selectedUserAddressIndex } = useContext(DeliveryServicesContext);
  const { bundleOffersSummary } = useBundleOffersSummary();
  const t = useTranslations('common.asides.side_cart');
  const r = useTranslations('common');
  const a = useTranslations('common.asides');
  const finalSumDescription = t('final_sum_description');
  const { openActionModal, close } = useContext(ModalContext);
  const [listAddtion, setListAddition] = useState();
  const defaultProductImage = useGetDefaultProductImage(-1);
  const [userIsOfLegalAge, setUserIsOfLegalAge] = useState(null);
  const [isAgeRestrictedProductsModalOpen, setIsAgeRestrictedProductsModalOpen] = useState(false);
  const { openAgeVerificationModal } = useAgeVerificationModal(setIsAgeRestrictedProductsModalOpen);
  const { config } = useConfiguration();

  const orderProductsAsString = JSON.stringify(orderProducts);
  useEffect(() => {
    async function getProps() {
      const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: -1 } }, Router.locale);
      const image = layoutProps?.emptyStates?.cart_empty_state?.url
        ? layoutProps?.emptyStates?.cart_empty_state?.url
        : null;
      setImage(image);
      const imageMin = layoutProps?.emptyStates?.minimum_order_value?.url
        ? layoutProps?.emptyStates?.minimum_order_value?.url
        : null;
      setMinOrderImage(imageMin);
    }
    getProps();
    const $listAddition = ListAdditionSheetStore.subscribe(setListAddition);
    const userIsOfLegalAge$ = UserIsOfLegalAgeStore.subscribe(setUserIsOfLegalAge);

    return () => {
      $listAddition.unsubscribe();
      userIsOfLegalAge$.unsubscribe();
    };
  }, []);
  const showMinimumLimitModal = minOrderLimit => {
    const url = minOrderImage ? minOrderImage : '/assets/wave-grocery/Bus.png';
    openActionModal({
      closeOnOuterClick: true,
      title: t('order_minimum_limit_title'),
      text: t('order_minimum_limit_description', { limit: minOrderLimit }),
      actions: [
        {
          btnLabel: t('return_to_cart'),
          onClick: () => {
            close();
          },
        },
      ],
      img: {
        url: url,
        ariaLabel: t('time_run_out_title'),
        size: 'medium',
      },
    });
  };

  const checkOrderEditHandler = () => {
    let finalResult;
    checkOrderEdit(
      selectedOrder?._id,
      tempProducts,
      selectedOrder,
      bundleOffersSummary,
      undefined,
      undefined,
      formatter,
      t('no_comment'),
      undefined,
      a
    )
      .then(result => {
        if (result && result.data) {
          fullCartStore.update(result);
          finalResult = result;
          setData(result);
          if (result?.progress) {
            const { title, data } = result?.progress;
            if (data?.remainingAmountForFree) {
              setFormattedTitle(
                title
                  .replace('{remainingAmountForFree}', data.remainingAmountForFree.toFixed(2))
                  .replace('{currency}', data.currency)
              );
            } else {
              setFormattedTitle(
                title.replace('{remainingAmount}', data.remainingAmount).replace('{currency}', data.currency)
              );
            }
          }
        }
      })
      .finally(() => {
        if (finalResult?.products?.length > 0) {
          setIsLoading(true);
          onSelect('UPDATE_DETAILS', false);
        }
      });
  };

  const onSubmit = () => {
    if (data?.data?.messages?.some(el => el.code === 'MSG_ORDER00017')) {
      showMinimumLimitModal(data?.progress.progressInfo.requiredValue / 100);
      return;
    }

    const hasNotVerifiedAge = UserIsOfLegalAgeStore.getValue() === null;
    const editedOrderHasAgeRestrictedProducts = data?.data?.products?.some(prod => prod?.needsAgeVerification) ?? false;
    const userIsOfLegalAge = UserIsOfLegalAgeStore.getValue();
    if (editedOrderHasAgeRestrictedProducts && hasNotVerifiedAge) {
      openAgeVerificationModal();
      return;
    }

    if (editedOrderHasAgeRestrictedProducts && !userIsOfLegalAge) {
      setIsAgeRestrictedProductsModalOpen(true);
      return;
    }

    checkOrderEditHandler();
  };

  useEffect(() => {
    if (!productsForOrder.getValue()) return;
    const products = orderProducts?.flatMap(el => {
      if (el.cartItem.finalPrice !== 0) {
        return Array.from({ length: el.cartItem.quantity }, () => ({
          ...el.cartItem,
          ...el.definition,
          id: el.definition._id,
          myoId: el.cartItem?.options[0]?.id ? el.definition._id + '_' + el.cartItem.options[0]?.id : undefined,
        }));
      } else {
        return [];
      }
    });

    if (productsForOrder.getValue().length === 0) {
      if (products) {
        productsForOrder.update(products);
      }
    }
  }, [orderProductsAsString]);
  useEffect(() => {
    let temp;
    const productsForOrder$ = productsForOrder.subscribe((productsOrder = []) => {
      temp = setCartProducts(productsOrder);
      setTempProducts(temp);
    });

    //we use getValue because it gets the value from storage not from local state which is asynchronous
    const hasNotVerifiedAge = UserIsOfLegalAgeStore.getValue() === null;
    const userIsOfLegalAge = UserIsOfLegalAgeStore.getValue();

    const cartPrice$ = cartPrice.subscribe(setTotalPrice);

    if (selectedOrder) {
      checkOrderEdit(
        selectedOrder._id,
        temp,
        selectedOrder,
        bundleOffersSummary,
        undefined,
        undefined,
        formatter,
        t('no_comment'),
        undefined,
        a
      )
        .then(result => {
          if (result && result.data) {
            if (!userIsOfLegalAge || hasNotVerifiedAge) {
              const productsWithAgeRestrictedLabel = result?.products?.map(prod => {
                const ageRestrictedProductFound = result?.data?.products?.find(
                  ageRestProd => ageRestProd._id === prod.id && ageRestProd?.needsAgeVerification
                );
                if (ageRestrictedProductFound) {
                  return {
                    ...prod,
                    hasDeleteButton: false,
                    label: a('age_verification.label'),
                    labelType: 'ageRestricted',
                    isInAgeRestrictedModal: false,
                    userIsOfLegalAge: false,
                  };
                }

                return prod;
              });

              result.products = productsWithAgeRestrictedLabel;
            }
            setData(result);
            if (result?.progress) {
              const { title, data } = result?.progress;
              if (data?.remainingAmountForFree) {
                setFormattedTitle(
                  title
                    .replace('{remainingAmountForFree}', data.remainingAmountForFree.toFixed(2))
                    .replace('{currency}', data.currency)
                );
              } else {
                setFormattedTitle(
                  title.replace('{remainingAmount}', data.remainingAmount).replace('{currency}', data.currency)
                );
              }
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    const flag = data?.order?.products?.length > 0;

    setProductsFiltered(flag ? data?.order?.products : tempProducts);

    return () => {
      cartPrice$.unsubscribe();

      productsForOrder$.unsubscribe();
    };
  }, [remove, orderProductsAsString, selectedOrder?.id, listAddtion, userIsOfLegalAge]);

  useEffect(() => {
    setTotalPrice(
      formatter.format(
        (data?.data?.order?.orderItemsPriceWithoutDiscounts - setDiscounts(data?.data?.order?.discounts, 'PRODUCTS')) /
          100
      )
    );
    cartPrice.update(
      (
        (data?.data?.order?.orderItemsPriceWithoutDiscounts - setDiscounts(data?.data?.order?.discounts, 'PRODUCTS')) /
        100
      ).toFixed(2)
    );
  }, [data]);

  const queryClient = useQueryClient();

  const prefetchProductData = (myId, myLocale) => {
    if (myId) {
      queryClient.prefetchQuery({
        queryKey: ['product', myId, myLocale],
        queryFn: () => getProductInfoV3(myId, myLocale, process.env.PRIVATE_API_KEY),
      });
      queryClient.prefetchQuery({
        queryKey: ['product-details', myId, myLocale],
        queryFn: () => getProductDetailsV3(myId, myLocale, process.env.PRIVATE_API_KEY),
      });
    }
  };

  useEffect(() => {
    // Prefetch product details so that they don't appear with empty ingredients, might need to fix later
    const products = productsForOrder.getValue();
    const ids = new Set(products.map(i => i.productId));
    for (const id of ids) {
      prefetchProductData(id, router.locale);
    }
  }, [router.locale]);

  const sideCartEditItems = useMemo(() => {
    if (!data?.products) return [];

    if (config?.catalog?.product?.packagingFee?.enabled) {
      return data?.products?.map(prod => {
        const matchedProduct = data?.data?.order?.products?.find(item => item.productId === prod.id);
        if (matchedProduct?.packagingProductId && matchedProduct?.packagingUnitPrice) {
          return {
            ...prod,
            packagingProductId: matchedProduct?.packagingProductId,
            packagingUnitPrice: formatter.format(Number(matchedProduct?.packagingUnitPrice / 100)),
            packagingProductName: matchedProduct?.packagingProductName,
          };
        } else return prod;
      });
    } else return data?.products;
  }, [data, config]);

  if (!orderProducts) return null;

  return (
    <>
      <SideCartEdit
        hasProgressBar={!isUndefined(data?.progress) ?? false}
        progressBarDescription={!isUndefined(data?.progress) && formattedTitle}
        progressBarProgress={
          (data?.progress?.progressInfo?.currentValue * 100) / data?.progress?.progressInfo?.requiredValue ?? 0
        }
        content={{
          items: sideCartEditItems,
          empty: sideCartEditItems.length === 0,
          emptyTitle: sideCartEditItems.length === 0 && t('empty_title'),
          emptyText: sideCartEditItems.length === 0 && t('empty_text'),
          emptyImg: image ? image : EmptyState.src,
        }}
        productValueLabel={
          data?.data?.order?.fees?.some(el => el.type === 'SHIPPING_COST')
            ? data?.data?.order?.fees?.filter(el => el.type === 'SHIPPING_COST')[0].title
            : undefined
        }
        productValue={
          data?.data?.order?.fees?.some(el => el.type === 'SHIPPING_COST')
            ? formatter.format(data?.data?.order?.fees?.filter(el => el.type === 'SHIPPING_COST')[0]?.amount / 100)
            : undefined
        }
        finalSumDescription={finalSumDescription}
        finalSumValue={formatter.format(
          (data?.data?.order?.orderItemsPriceWithoutDiscounts -
            setDiscounts(data?.data?.order?.discounts, 'PRODUCTS')) /
            100
        )}
        onChange={(id, ingredients) => {
          onAdd(id, setTempProducts, setRemove, remove, data, undefined, ingredients);
        }}
        onRemove={(id, ingredients) => {
          onRemove(id, setTempProducts, setRemove, remove, undefined, undefined, ingredients);
        }}
        onAddList={item => {
          openListSideSheet(item);
        }}
        isLoading={isLoading}
        actionButtons={[
          {
            btnLabel: t('btn_label'),
            onClick: () => onSubmit(),
            variant: 'filled',
            key: 'filled',
          },
        ]}
        onOpenDetails={product => {
          onOpenDetails(product.id, undefined, undefined, product?.ingredients);
        }}
        listButtonText={r('lists_tile_item')}
        removeButtonText={r('remove_all_tile_item')}
        removeAll={(id, ingredients) => {
          onRemoveAll(id, setTempProducts, setRemove, remove, undefined, undefined, ingredients);
        }}
        progressBarEmoji={data?.data?.messages?.some(el => el.code === 'MSG_ORDER00017') ? '🙄' : '🚚'}
        defaultProductImage={defaultProductImage}
        isPackagingFeeEnabled={config?.catalog?.product?.packagingFee?.enabled}
      />
      {isAgeRestrictedProductsModalOpen && (
        <AgeRestrictedProductsModal
          isOpen={isAgeRestrictedProductsModalOpen}
          modalClose={() => setIsAgeRestrictedProductsModalOpen(false)}
          isInEditMode={true}
          checkOrderEditHandler={checkOrderEditHandler}
        />
      )}
    </>
  );
}
